import { Link } from '@tanstack/react-router';
import {
  parseUpgradeRequired,
  upgradeRequiredMessage,
  type UpgradeRequiredDetails,
} from '@wordpuppi/shared';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { Sparkles } from 'lucide-react';

/**
 * Reusable 402 capability-denial surface (AB#465). Feed it either a caught error
 * or already-parsed details; renders a friendly upsell plus a link to Billing.
 * Returns null when the error isn't an upgrade-required 402, so a route can drop
 * `<UpgradeNotice error={mutation.error} />` in unconditionally and it only shows
 * when a feature was actually plan-gated. Keeps the seat-limit 402 (handled on
 * /team) out of scope — parseUpgradeRequired ignores it.
 */
export function UpgradeNotice({
  error,
  details,
  className,
  canManageBilling = true,
}: {
  error?: unknown;
  details?: UpgradeRequiredDetails | null;
  className?: string;
  /** Site-scoped features must use the server's resolved billing-owner permission. */
  canManageBilling?: boolean;
}) {
  const info = details ?? parseUpgradeRequired(error);
  if (!info) return null;
  const plan = info.required_plan
    ? info.required_plan.charAt(0).toUpperCase() + info.required_plan.slice(1)
    : 'Pro';
  return (
    <div
      className={cn(
        'flex flex-wrap items-center justify-between gap-3 rounded-lg border border-primary/40 bg-primary/5 p-3 text-sm',
        // Shows up after a blocked action: fade and settle in rather than pop.
        'transition-[opacity,translate] duration-(--duration-dialog) ease-out starting:translate-y-1 starting:opacity-0 motion-reduce:starting:translate-y-0',
        className,
      )}
    >
      <div className="flex items-center gap-2 text-foreground">
        <Sparkles className="size-4 shrink-0 text-primary" aria-hidden="true" />
        <span>{upgradeRequiredMessage(info)}</span>
      </div>
      {canManageBilling ? <Button size="sm" asChild>
        <Link to="/account/billing">Upgrade to {plan}</Link>
      </Button> : <span>Ask the site's billing owner to upgrade to {plan}.</span>}
    </div>
  );
}
